import { httpClient } from '@/utils/httpClient';
import { outputReport, getOutputFormat } from '@/utils/reportOutput';
import { entityService } from '@/services/EntityService';
import { getPersonLabels } from '@/hooks/usePersonLabels';
import {
  buildOverdueAssetsReportHtml,
  type OverdueAssetsItem,
  type OverdueAssetsTemplateOptions,
} from '@/reports/templates/overdueAssetsTemplate';

interface ThrReminderCandidateRow {
  person_id: string;
  person_email: string | null;
  person_name: string;
  hand_receipt_id: string;
  hand_receipt_identifier: string;
  asset_id: string;
  asset_name: string;
  nsn: string | null;
  quantity: number;
  due_date: string;
}

interface AssetEntityRow {
  id: string;
  display_name: string;
  entity_data: {
    lin?: string;
    nsn?: string;
    catalog_id?: string;
  };
}

interface RawIeSummaryRow {
  lin?: string | null;
  authorized_qty?: number | null;
}

const chunk = <T>(values: T[], size: number): T[][] => {
  if (size <= 0) return [values];
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
};

const parseCsvList = (value: string): string[] =>
  value
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);

const parseCatalogUnitCost = (
  entityData: Record<string, unknown> | null | undefined,
): number | null => {
  const raw = entityData?.unit_price;
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : null;
  }
  if (typeof raw === 'string') {
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const formatRecoverableLabel = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const normalized = value.trim().toUpperCase();
  if (normalized.includes('NON')) return 'NonRecoverable';
  if (normalized.includes('RECOVERABLE')) return 'Recoverable';
  return value.trim();
};

export const runOverdueAssetsReport = async (params: Record<string, unknown>): Promise<void> => {
  const fromDate = (params.fromDate as string | undefined) ?? '';
  const toDate = (params.toDate as string | undefined) ?? '';
  const personParam = params.person as { id: string; displayName: string } | null | undefined;
  const individualName = personParam?.displayName ?? '';
  const ieFilter = (params.ieFilter as string | undefined) ?? '';
  const qtyAuthLowerThanIssued = Boolean(params.qtyAuthLowerThanIssued ?? false);

  // Use the existing THR reminder RPC with mode = 'overdue'
  const rpcEndpoint = '/api/v1/rpc/get_thr_reminder_candidates';
  const rows = await httpClient.post<ThrReminderCandidateRow[]>(rpcEndpoint, {
    p_mode: 'overdue',
    p_window_days: 365, // wide window; RPC still enforces due_date < today for 'overdue'
  });

  const fromDateValue = fromDate ? new Date(fromDate) : null;
  const toDateValue = toDate ? new Date(toDate) : null;
  const ieTokens = parseCsvList(ieFilter.toUpperCase());

  const filteredRows = rows.filter((row) => {
    const due = new Date(row.due_date);
    if (Number.isNaN(due.getTime())) {
      return false;
    }
    if (fromDateValue && due < fromDateValue) return false;
    if (toDateValue && due > toDateValue) return false;
    if (
      individualName &&
      !row.person_name.toLowerCase().includes(individualName.toLowerCase())
    ) {
      return false;
    }
    return true;
  });

  const assetIds = Array.from(
    new Set(
      filteredRows
        .map((row) => row.asset_id)
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0),
    ),
  );

  const assetById = new Map<string, AssetEntityRow>();
  for (const batchIds of chunk(assetIds, 75)) {
    const idFilter = batchIds.length === 1 ? `eq.${batchIds[0]}` : `in.(${batchIds.join(',')})`;
    const assets = await entityService.list({
      type: 'asset',
      limit: batchIds.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,display_name,entity_data',
      },
    });

    for (const asset of assets) {
      const entityData = (asset.entity_data ?? {}) as Record<string, unknown>;
      assetById.set(asset.id, {
        id: asset.id,
        display_name: asset.display_name,
        entity_data: {
          lin: typeof entityData.lin === 'string' ? entityData.lin : undefined,
          nsn: typeof entityData.nsn === 'string' ? entityData.nsn : undefined,
          catalog_id: typeof entityData.catalog_id === 'string' ? entityData.catalog_id : undefined,
        },
      });
    }
  }

  const catalogIds = Array.from(
    new Set(
      Array.from(assetById.values())
        .map((asset) => asset.entity_data.catalog_id)
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0),
    ),
  );

  const unitCostByCatalogId = new Map<string, number | null>();
  const recoverableByCatalogId = new Map<string, string | null>();

  for (const batchIds of chunk(catalogIds, 75)) {
    const idFilter = batchIds.length === 1 ? `eq.${batchIds[0]}` : `in.(${batchIds.join(',')})`;
    const catalogs = await entityService.list({
      type: 'catalog',
      limit: batchIds.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,entity_data',
      },
    });

    for (const catalog of catalogs) {
      const entityData = (catalog.entity_data ?? {}) as Record<string, unknown>;
      unitCostByCatalogId.set(catalog.id, parseCatalogUnitCost(entityData));

      const rawRecoverable = entityData.recoverable_nonrecoverable;
      recoverableByCatalogId.set(
        catalog.id,
        typeof rawRecoverable === 'string' ? formatRecoverableLabel(rawRecoverable) : null,
      );
    }
  }

  // Authorized qty lookup: IE Kit summary per person, keyed by LIN
  const personIds = Array.from(new Set(filteredRows.map((row) => row.person_id)));
  const authorizedQtyByPersonAndLin = new Map<string, number>();

  for (const personId of personIds) {
    const query = new URLSearchParams({
      person_id: `eq.${personId}`,
      select: 'lin,authorized_qty',
      limit: '10000',
    }).toString();

    const ieRows = await httpClient.get<RawIeSummaryRow[]>(`/api/v1/ie_summary?${query}`);
    for (const ieRow of ieRows) {
      const lin = typeof ieRow.lin === 'string' ? ieRow.lin.trim().toUpperCase() : '';
      if (!lin) continue;
      const qty = ieRow.authorized_qty ?? 0;
      authorizedQtyByPersonAndLin.set(`${personId}|${lin}`, qty);
    }
  }

  const items: OverdueAssetsItem[] = filteredRows
    .map((row): OverdueAssetsItem | null => {
      const asset = assetById.get(row.asset_id);
      const lin = asset?.entity_data.lin ?? null;
      const nsn = asset?.entity_data.nsn ?? row.nsn ?? null;
      const nomenclature = asset?.display_name ?? row.asset_name ?? '—';

      if (ieTokens.length > 0) {
        const normalizedLin = lin ? lin.trim().toUpperCase() : '';
        if (!normalizedLin || !ieTokens.includes(normalizedLin)) {
          return null;
        }
      }

      const catalogId = asset?.entity_data.catalog_id ?? null;
      const unitCost = catalogId ? (unitCostByCatalogId.get(catalogId) ?? null) : null;
      const recoverableFlag = catalogId ? (recoverableByCatalogId.get(catalogId) ?? null) : null;

      const issuedQty = row.quantity ?? 0;
      const authorizedQty = lin
        ? (authorizedQtyByPersonAndLin.get(`${row.person_id}|${lin.trim().toUpperCase()}`) ?? 0)
        : 0;

      if (qtyAuthLowerThanIssued && issuedQty <= authorizedQty) {
        return null;
      }

      const totalCost =
        typeof unitCost === 'number' && Number.isFinite(unitCost) ? unitCost * issuedQty : null;

      const item: OverdueAssetsItem = {
        individualName: row.person_name,
        individualIe: row.hand_receipt_identifier,
        lin,
        nsn,
        nomenclature,
        cost: unitCost,
        totalCost,
        recoverableFlag,
        quantityIssued: issuedQty,
        quantityAuthorized: authorizedQty,
        thrPhr: 'THR',
        dueDate: row.due_date,
      };

      return item;
    })
    .filter((item): item is OverdueAssetsItem => item !== null);

  const totalCount = items.length;
  const totalCost = items.reduce((sum, item) => {
    if (typeof item.totalCost === 'number' && Number.isFinite(item.totalCost)) {
      return sum + item.totalCost;
    }
    return sum;
  }, 0);

  const ieKitLabel = (params.ieKitLabel as string | undefined) ?? 'IE Kit';

  const options: OverdueAssetsTemplateOptions = {
    personLabels: getPersonLabels(params),
    generatedAt: new Date(),
    header: {
      fromDate,
      toDate,
      individualName,
      individualIe: undefined,
      ieFilter: ieFilter.trim() || null,
      qtyAuthLowerThanIssued,
      totalCost: totalCost > 0 ? totalCost : null,
      totalCount,
    },
    items,
    ieKitLabel,
  };

  const html = buildOverdueAssetsReportHtml(options);
  outputReport(html, 'overdue-assets.html', getOutputFormat(params));
};
